/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useMemo } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { Tooltip } from 'antd'
import Text from 'src/components/ts-components/Text'
import CopyComponent from 'src/components/CopyComponent'
import useWindowSize from 'src/hooks/useWindowSize'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import { RegistrationInfoSectionProps, InfoField } from './types'
import {
  calculateBillingTotals,
  formatBillingValue,
  getStatusInfo,
} from './utils'
import StatusIndicator from './StatusIndicator'
import { formatCurrency } from '@sportninja/common/utils/utils'

/**
 * Registration Info Section Component
 *
 * Displays registration metadata and billing summary in a two-row layout.
 * Follows the Figma design with dark background, bordered container,
 * and responsive column layout.
 */
const RegistrationInfoSection = ({
  registrationDetails,
  divisionInfo,
  isLoading = false,
  entityId,
}: RegistrationInfoSectionProps) => {
  const currency =
    divisionInfo?.currency_code?.toUpperCase() ||
    registrationDetails?.billing?.[0]?.currency_code?.toUpperCase() ||
    'CAD'
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const isPlayerRegistration = useMemo(
    () => registrationDetails?.registration_type !== 'Team',
    [registrationDetails?.registration_type]
  )

  const billingTotals = useMemo(
    () =>
      calculateBillingTotals(
        registrationDetails?.billing,
        isPlayerRegistration
      ),
    [registrationDetails?.billing, isPlayerRegistration]
  )

  const statusInfo = useMemo(
    () => getStatusInfo(registrationDetails?.status_id),
    [registrationDetails?.status_id]
  )

  if (isLoading || !registrationDetails) {
    return null
  }

  // Define data row 1 fields - filter out null/unavailable fields
  const row1Fields = (
    [
      divisionInfo?.name
        ? { label: 'Division', value: divisionInfo.name }
        : null,
      registrationDetails?.team?.name
        ? { label: 'Team', value: registrationDetails.team.name }
        : null,
      // Only show Players for Team registrations
      !isPlayerRegistration &&
      divisionInfo?.totalPlayers !== undefined &&
      divisionInfo?.totalPlayers !== null
        ? { label: 'Players', value: divisionInfo.totalPlayers.toString() }
        : null,
      registrationDetails?.created_at
        ? {
            label: 'Registered At',
            value: formatShortDate(registrationDetails.created_at),
          }
        : null,
      {
        label: 'Reg. Status',
        value: registrationDetails?.status || statusInfo.label,
        renderValue: () => (
          <StatusIndicator
            statusId={registrationDetails?.status_id || 3}
            statusLabel={registrationDetails?.status || statusInfo.label}
          />
        ),
      },
      // Add empty placeholder at the end for Player registrations to maintain grid alignment
      isPlayerRegistration ? { label: '', value: '', isEmpty: true } : null,
    ] as (InfoField | null)[]
  ).filter(
    (field): field is InfoField =>
      field !== null && (field.value !== null || field.isEmpty === true)
  )

  const hasBillingData = (registrationDetails?.billing?.length || 0) > 0
  const noCost =
    !hasBillingData &&
    (isPlayerRegistration ? !divisionInfo?.playerCost : !divisionInfo?.cost)

  // Define data row 2 fields - billing totals
  const row2Fields: InfoField[] = [
    {
      label: isPlayerRegistration ? 'Player Cost' : 'Team Cost',
      value: (() => {
        const cost = isPlayerRegistration
          ? divisionInfo?.playerCost
          : divisionInfo?.cost
        if (cost) return formatCurrency(cost, currency)
        if (billingTotals.teamCost)
          return formatCurrency(billingTotals.teamCost, currency)
        return '-'
      })(),
    },
    {
      label: 'Outstanding',
      value: noCost
        ? '-'
        : formatBillingValue(billingTotals.outstanding, currency),
    },
    {
      label: 'Paid',
      value: noCost ? '-' : formatBillingValue(billingTotals.paid, currency),
    },
    {
      label: 'Total Outstanding',
      value: noCost
        ? '-'
        : formatBillingValue(billingTotals.totalOutstanding, currency),
    },
    {
      label: 'Total Paid',
      value: noCost
        ? '-'
        : formatBillingValue(billingTotals.totalPaid, currency),
    },
  ]

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 16px;
        gap: 16px;
        background-color: ${colors.SECONDARY_200};
        border: 1px solid ${colors.SECONDARY_100};
        border-radius: 8px;
        margin-bottom: 16px;
      `}
    >
      {/* Header Row: Title + Registration ID */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        `}
      >
        {/* Title */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <Text
            variant='captionSmall'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              letter-spacing: 1.68px;
              text-transform: uppercase;
            `}
          >
            Registration Info
          </Text>
          {registrationDetails?.is_coupon && registrationDetails?.coupon && (
            <Tooltip
              placement='top'
              zIndex={11000}
              title={
                <div>
                  <div>
                    <strong>Coupon Code:</strong>{' '}
                    {registrationDetails.coupon.code}
                  </div>
                  <div>
                    <strong>Discount:</strong>{' '}
                    {registrationDetails.coupon.discount_type === 'percentage'
                      ? `${registrationDetails.coupon.discount}%`
                      : formatCurrency(
                          registrationDetails.coupon.discount_amount ||
                            registrationDetails.coupon.discount,
                          currency
                        )}
                  </div>
                  <div>
                    <strong>Type:</strong>{' '}
                    {registrationDetails.coupon.discount_type === 'percentage'
                      ? 'Percentage'
                      : 'Amount'}
                  </div>
                </div>
              }
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                height='20px'
                viewBox='0 -960 960 960'
                width='20px'
                fill={colors.PRIMARY}
                css={css`
                  cursor: pointer;
                  flex-shrink: 0;
                `}
              >
                <path d='M856-390 570-104q-12 12-27 18t-30 6q-15 0-30-6t-27-18L103-457q-11-11-17-25.5T80-513v-287q0-33 23.5-56.5T160-880h287q16 0 31 6.5t26 17.5l352 353q12 12 17.5 27t5.5 30q0 15-5.5 29.5T856-390ZM513-160l286-286-353-354H160v286l353 354ZM260-640q25 0 42.5-17.5T320-700q0-25-17.5-42.5T260-760q-25 0-42.5 17.5T200-700q0 25 17.5 42.5T260-640Zm220 160Z' />
              </svg>
            </Tooltip>
          )}
        </div>

        {/* ID Copy Components */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
          `}
        >
          {registrationDetails?.uid && (
            <CopyComponent
              customText='Registration ID'
              id={registrationDetails.uid}
              alternativeBackground={false}
            />
          )}
          {entityId?.id && (
            <CopyComponent
              customText={entityId.label}
              id={entityId.id}
              alternativeBackground={false}
            />
          )}
        </div>
      </div>

      {/* Data Rows Container */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        {/* Data Row 1 */}
        {row1Fields.length > 0 && (
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: ${isMobile ? '16px' : '16px'};

              @media (max-width: 768px) {
                gap: 16px;
              }
            `}
          >
            {row1Fields.map((field, index) => (
              <div
                key={`row1-${index}`}
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 0;
                  flex: 1;
                  min-width: ${isMobile ? '45%' : '120px'};

                  @media (max-width: 768px) {
                    min-width: 45%;
                  }
                `}
              >
                {!field.isEmpty && (
                  <>
                    <Text
                      variant='paragraphXSmall'
                      weight='medium'
                      color={colors.NEUTRAL_50}
                    >
                      {field.label}
                    </Text>
                    {field.renderValue ? (
                      field.renderValue()
                    ) : (
                      <Text
                        variant='paragraphSmall'
                        weight='regular'
                        color={colors.WHITE}
                      >
                        {field.value}
                      </Text>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Separator Line */}
        {row1Fields.length > 0 && row2Fields.length > 0 && (
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SECONDARY_100};
              width: 100%;
            `}
          />
        )}

        {/* Data Row 2 - Billing Totals */}
        {row2Fields.length > 0 && (
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: ${isMobile ? '16px' : '16px'};

              @media (max-width: 768px) {
                gap: 16px;
              }
            `}
          >
            {row2Fields.map((field, index) => (
              <div
                key={`row2-${index}`}
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 0;
                  flex: 1;
                  min-width: ${isMobile ? '45%' : '120px'};

                  @media (max-width: 768px) {
                    min-width: 45%;
                  }
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='medium'
                  color={colors.NEUTRAL_50}
                >
                  {field.label}
                </Text>
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                >
                  {field.value}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default RegistrationInfoSection
